
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || 'https://bwrjzklcyhddlpzbjlgd.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJ3cmp6a2xjeWhkZGxwemJqbGdkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjI5MTM1NjgsImV4cCI6MjA3ODQ4OTU2OH0.W5h2KDWanDhW_Ydti6DC8iTku3O2-hajZACrPGQWpdw';

console.log('Supabase URL:', supabaseUrl);
console.log('Supabase Key:', supabaseAnonKey ? 'Configurada' : 'Não configurada');

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Variáveis de ambiente do Supabase não encontradas');
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  global: {
    headers: {
      'Content-Type': 'application/json',
    },
  },
});

export interface Card {
  id: string;
  user_id: string;
  title: string;
  thumbnail: string;
  data: {
    code?: string | any; // Para JSON e compatibilidade retroativa
    description?: string;
    // Novos campos para HTML/CSS/JS separados
    html_code?: string;
    css_code?: string;
    js_code?: string;
    html_instructions?: string;
    css_instructions?: string;
    js_instructions?: string;
    [key: string]: any; // Para permitir outros campos como type, elements, etc.
  };
  category: string;
  categories?: string[];
  code_type?: 'json' | 'css' | 'js' | 'html' | 'other';
  code_hash?: string; // Hash SHA-256 do código para detecção de duplicados
  is_editable?: boolean;
  created_at: string;
}

// Função auxiliar para extrair o código do campo data
export function getCardCode(data: Card['data']): string {
  if (!data || typeof data !== 'object') {
    return '';
  }
  
  // Se tiver campo code, retorna o code (para JSON e compatibilidade)
  if ('code' in data && data.code !== undefined && data.code !== null) {
    const codeValue = data.code;
    // Se code for uma string, retorna direto
    if (typeof codeValue === 'string') {
      return codeValue;
    }
    // Se code for um objeto (como JSON do Elementor), retorna stringificado
    if (typeof codeValue === 'object') {
      return JSON.stringify(codeValue, null, 2);
    }
    return String(codeValue);
  }
  
  // Se for um objeto complexo (como Elementor com type), retorna o JSON completo stringificado
  if ('type' in data || 'elements' in data) {
    return JSON.stringify(data, null, 2);
  }
  
  // Caso contrário, retorna o objeto stringificado
  return JSON.stringify(data, null, 2);
}

// Função auxiliar para verificar se o card tem códigos separados (HTML/CSS/JS)
export function hasSeparatedCodes(data: Card['data']): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }
  return !!(data.html_code || data.css_code || data.js_code);
}

// Função auxiliar para obter código HTML
export function getHtmlCode(data: Card['data']): string {
  if (!data || typeof data !== 'object') {
    return '';
  }
  return data.html_code || '';
}

// Função auxiliar para obter código CSS
export function getCssCode(data: Card['data']): string {
  if (!data || typeof data !== 'object') {
    return '';
  }
  return data.css_code || '';
}

// Função auxiliar para obter código JavaScript
export function getJsCode(data: Card['data']): string {
  if (!data || typeof data !== 'object') {
    return '';
  }
  return data.js_code || '';
}

// Função auxiliar para obter instruções HTML
export function getHtmlInstructions(data: Card['data']): string {
  if (!data || typeof data !== 'object') {
    return '';
  }
  return data.html_instructions || '';
}

// Função auxiliar para obter instruções CSS
export function getCssInstructions(data: Card['data']): string {
  if (!data || typeof data !== 'object') {
    return '';
  }
  return data.css_instructions || '';
}

// Função auxiliar para obter instruções JS
export function getJsInstructions(data: Card['data']): string {
  if (!data || typeof data !== 'object') {
    return '';
  }
  return data.js_instructions || '';
}

// Função auxiliar para extrair a descrição do campo data
export function getCardDescription(data: Card['data']): string {
  if (!data || typeof data !== 'object') {
    return '';
  }
  
  if ('description' in data && data.description) {
    return String(data.description);
  }
  
  return '';
}

export const codeTypes = [
  { value: 'json', label: 'JSON', icon: 'ri-braces-line' },
  { value: 'css', label: 'CSS', icon: 'ri-css3-line' },
  { value: 'js', label: 'JavaScript', icon: 'ri-javascript-line' },
  { value: 'html', label: 'HTML', icon: 'ri-html5-line' },
  { value: 'other', label: 'Outro', icon: 'ri-code-line' }
];
